import { useState, useEffect } from 'react';
import { Field, FieldProps } from 'formik';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import StarIconClosed from 'packages/kakapo-ui-kit/assets/icons/StarIconClosed';
import StarIconOpen from 'packages/kakapo-ui-kit/assets/icons/StarIconOpen';
import Nullable from 'packages/kakapo-ui-services/types/Nullable';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';
import FieldErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/FieldErrorMessage';

interface IProps {
	name: string;
	numberOfStars?: number;
	ratingLabelKey?: string;
}

interface IElementProps {
	name: string;
	value?: number;
	numberOfStars?: number;
	ratingLabelKey?: string;
	disabled?: boolean;

	// Returns the numeric value of stars (counts from 1)
	onChange?: (value: number) => void;
}

const Container = styled.div`
	margin-top: -4px;
`;

const Star = styled.button`
	background-color: transparent;
	display: inline-block;
	margin-right: 2px;

	${(props) =>
		props.disabled
			? `
		cursor: default;
	`
			: ''}
`;

const StarText = styled(ParagraphSmall)`
	display: inline-block;
	margin-left: 10px;
	transform: translateY(-5px);
`;

export const StarRatingElement = ({
	name,
	value,
	onChange,
	disabled,
	numberOfStars = 5,
	ratingLabelKey = 'common.fields.starRating',
}: IElementProps) => {
	const [selectedValue, setSelectedValue] = useState<Nullable<number>>(value ?? null);
	const [hoverValue, setHoverValue] = useState<Nullable<number>>(null);
	const { t } = useTranslation();

	useEffect(() => {
		if (selectedValue != null && onChange != null) {
			onChange(selectedValue);
		}
	}, [selectedValue]);

	const activeIndex = (hoverValue != null ? hoverValue : selectedValue) ?? null;

	return (
		<Container
			onMouseLeave={() => {
				setHoverValue(null);
			}}
		>
			{Array(numberOfStars)
				.fill(null)
				.map((_, index) => {
					// Star values are starting from 1, not from 0
					const adjustedIndex = index + 1;

					// Please only use the variable `adjustedIndex` below, do not refer to
					// `index` directly
					const isActive = activeIndex != null && activeIndex >= adjustedIndex;

					return (
						<Star
							key={`${name}-${adjustedIndex}`}
							disabled={disabled}
							type="button"
							onClick={() => {
								if (disabled !== true) {
									setSelectedValue(adjustedIndex);
								}
							}}
							onMouseEnter={() => {
								if (disabled !== true) {
									setHoverValue(adjustedIndex);
								}
							}}
						>
							{isActive ? <StarIconClosed /> : <StarIconOpen />}
						</Star>
					);
				})}

			{activeIndex != null && (
				<StarText color="slateBlue">{t(`${ratingLabelKey}.${activeIndex}`)}</StarText>
			)}
		</Container>
	);
};

export default function StarRating({ name, numberOfStars, ratingLabelKey }: IProps) {
	return (
		<>
			<Field name={name}>
				{({ field: { value }, form: { setFieldValue } }: FieldProps<number>) => (
					<StarRatingElement
						name={name}
						onChange={(changedValue) => {
							setFieldValue(name, changedValue);
						}}
						ratingLabelKey={ratingLabelKey}
						numberOfStars={numberOfStars}
						value={value}
					/>
				)}
			</Field>

			<FieldErrorMessage name={name} />
		</>
	);
}
